import { useState } from "react";
import { X, Loader2, MessageCircle, Check } from "lucide-react";
import api from "@/lib/api";

/* Lead-capture modal used by every "Contact sales" / "Email us" button on the
   public landing page.  Captures name+email+phone+optional message+company,
   POSTs to /api/leads, and on success either:
     - redirects the visitor into WhatsApp (if WA_BUSINESS_NUMBER is configured), or
     - shows a thank-you state.
*/
export default function ContactSalesModal({ open, onClose, source = "landing-contact-sales" }) {
  const [f, setF] = useState({ name: "", email: "", phone: "", company: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(false);

  if (!open) return null;

  const submit = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try {
      const { data } = await api.post("/leads", { ...f, source });
      if (data.whatsapp_link) {
        // Open WhatsApp in a new tab so we don't lose the user's spot on the landing page
        window.open(data.whatsapp_link, "_blank", "noopener,noreferrer");
      }
      setDone(true);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Couldn't send. Please try again.");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose} data-testid="contact-modal">
      <div className="bg-white text-[#0A0A0A] w-full max-w-lg border border-[#0A0A0A] shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between p-6 border-b border-[#DEE2E6]">
          <div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-[#FF4F00] font-mono">Contact sales</div>
            <h2 className="font-heading text-2xl font-bold tracking-tighter mt-1">Talk to us about pricing</h2>
            <p className="text-sm text-[#495057] mt-1.5">We&apos;ll reply within 1 business day — or hop on WhatsApp right after you submit.</p>
          </div>
          <button onClick={onClose} className="text-[#495057] hover:text-[#0A0A0A]" data-testid="contact-modal-close" aria-label="Close">
            <X size={20} />
          </button>
        </div>

        {done ? (
          <div className="p-8 text-center" data-testid="contact-done">
            <div className="inline-flex items-center gap-2 text-[#0B7A75] font-medium">
              <Check size={18} /> Got it — we&apos;ll be in touch within 1 business day.
            </div>
            <div className="mt-2 text-sm text-[#495057]">If WhatsApp didn&apos;t open automatically, please check your popup blocker.</div>
            <button onClick={onClose} data-testid="contact-done-close"
              className="mt-6 inline-flex items-center gap-2 bg-[#0A0A0A] hover:bg-[#FF4F00] text-white px-5 py-2.5 text-sm font-medium">
              Done
            </button>
          </div>
        ) : (
          <form onSubmit={submit} className="p-6 grid sm:grid-cols-2 gap-4" data-testid="contact-form">
            <Field label="Full name" required value={f.name} onChange={(v) => setF({ ...f, name: v })} testid="contact-name" />
            <Field label="Work email" type="email" required value={f.email} onChange={(v) => setF({ ...f, email: v })} testid="contact-email" />
            <Field label="Phone (with country code)" required value={f.phone} onChange={(v) => setF({ ...f, phone: v })} placeholder="+233 24 123 4567" testid="contact-phone" />
            <Field label="Company" value={f.company} onChange={(v) => setF({ ...f, company: v })} testid="contact-company" />
            <div className="sm:col-span-2">
              <label className="block text-xs uppercase tracking-[0.15em] text-[#495057] mb-2">How can we help? (Optional)</label>
              <textarea value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} data-testid="contact-message" rows={3}
                placeholder="Team size, modules of interest, timeline..."
                className="w-full bg-white border border-[#DEE2E6] px-4 py-3 text-sm focus:border-[#0A0A0A] focus:outline-none" />
            </div>
            {err && <div className="sm:col-span-2 text-sm text-[#D90429]" data-testid="contact-error">{err}</div>}
            <div className="sm:col-span-2 flex items-center justify-between gap-3 pt-1">
              <div className="text-[11px] text-[#495057] flex items-center gap-1.5">
                <MessageCircle size={12} className="text-[#25D366]" /> We&apos;ll open WhatsApp right after you submit.
              </div>
              <button type="submit" disabled={busy} data-testid="contact-submit"
                className="inline-flex items-center gap-2 bg-[#0A0A0A] hover:bg-[#FF4F00] disabled:opacity-60 text-white px-6 py-3 text-sm font-medium">
                {busy ? <><Loader2 size={14} className="animate-spin" /> Sending…</> : <>Continue to WhatsApp</>}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, testid, placeholder }) {
  return (
    <label className="block">
      <span className="block text-xs uppercase tracking-[0.15em] text-[#495057] mb-2">{label}{required && " *"}</span>
      <input value={value} type={type} required={required} placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-full bg-white border border-[#DEE2E6] px-4 py-3 text-sm focus:border-[#0A0A0A] focus:outline-none" />
    </label>
  );
}
